'use client'

import { useMemo } from 'react'
import { useTranslations } from 'next-intl'

import * as Dialog from '@radix-ui/react-dialog'

import { useShareFeedbackPhase } from '@common/core/components/ShareFeedback'

import { HelpDrawerFeedbackView, HelpDrawerMenuView } from './components'
import { useHelpDrawerView } from './hooks'
import type { HelpDrawerProps } from './types'
import { groupItemsIntoSections } from './utils'

import styles from './HelpDrawer.module.scss'

export function HelpDrawer({ isOpen, onClose, items, onItemClick, title = 'Help' }: HelpDrawerProps) {
  const t = useTranslations('feedback')
  const sections = useMemo(() => groupItemsIntoSections(items), [items])

  const { isFeedbackView, handleFeedbackOpenChange, handleItemClick, navigateToMenu } = useHelpDrawerView({
    isOpen,
    onClose,
    onItemClick,
  })

  const { phase, handleSubmittingChange, handleSuccess } = useShareFeedbackPhase({
    open: isOpen && isFeedbackView,
    onOpenChange: handleFeedbackOpenChange,
  })

  const feedbackTitle = t('create.title')
  const headerTitle = isFeedbackView ? feedbackTitle : title

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()} modal={!isFeedbackView}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <Dialog.Title className={styles.srOnly}>{headerTitle}</Dialog.Title>

          <div className={styles.dragBar}>
            <span className={styles.dragHandle} aria-hidden="true" />
          </div>

          <div className={`${styles.viewsContainer} ${isFeedbackView ? styles.viewsFeedback : styles.viewsMenu}`}>
            <HelpDrawerMenuView
              title={title}
              sections={sections}
              isHidden={isFeedbackView}
              onItemClick={handleItemClick}
            />

            <HelpDrawerFeedbackView
              feedbackTitle={feedbackTitle}
              closeAriaLabel={t('create.closeAriaLabel')}
              isHidden={!isFeedbackView}
              phase={phase}
              onSubmittingChange={handleSubmittingChange}
              onSuccess={handleSuccess}
              onBack={navigateToMenu}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
